import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Shield, Search, Activity, Users, FileText, DollarSign,
  Clock, ChevronLeft, ChevronRight, BarChart3, CalendarDays,
  LogIn, UserPlus, UserMinus, CheckCircle, XCircle, Edit, Trash2,
  Upload, Award, Briefcase, Filter
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API_URL } from '../lib/apiBase';
const ACTION_ICONS = {
  login: LogIn, create: UserPlus, update: Edit, delete: Trash2,
  deactivate: UserMinus, exit: UserMinus, apply: FileText,
  leave_approve: CheckCircle, leave_reject: XCircle,
  generate: DollarSign, finance_verify: CheckCircle, finance_reject: XCircle,
  mark_paid: DollarSign, bulk_upload: Upload, verify: CheckCircle, reject: XCircle,
  user_approve: CheckCircle, user_reject: XCircle,
};

const ACTION_COLORS = {
  login: 'bg-blue-100 text-blue-700',
  create: 'bg-green-100 text-green-700',
  update: 'bg-amber-100 text-amber-700',
  delete: 'bg-red-100 text-red-700',
  deactivate: 'bg-red-100 text-red-700',
  exit: 'bg-red-100 text-red-700',
  apply: 'bg-blue-100 text-blue-700',
  leave_approve: 'bg-green-100 text-green-700',
  leave_reject: 'bg-red-100 text-red-700',
  generate: 'bg-indigo-100 text-indigo-700',
  finance_verify: 'bg-green-100 text-green-700',
  finance_reject: 'bg-red-100 text-red-700',
  mark_paid: 'bg-emerald-100 text-emerald-700',
  bulk_upload: 'bg-purple-100 text-purple-700',
  verify: 'bg-green-100 text-green-700',
  reject: 'bg-red-100 text-red-700',
  user_approve: 'bg-green-100 text-green-700',
  user_reject: 'bg-red-100 text-red-700',
};

const RESOURCE_ICONS = {
  auth: LogIn, employee: Users, leave: FileText, payroll: DollarSign,
  user: Users, entity: Briefcase, role: Shield,
  lnd_activity: Award, ats_position: Briefcase,
};

function h() {
  const token = localStorage.getItem('hrms-token');
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

/* ====================================================================
   STATS DASHBOARD TAB
   ==================================================================== */
function StatsDashboard({ entityId }) {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (entityId) params.append('entity_id', entityId);
      const res = await fetch(`${API_URL}/api/audit-logs/stats?${params}`, { headers: h(), credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [days, entityId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded animate-pulse" />)}</div>;
  if (!stats) return <p className="text-slate-500">No data</p>;

  return (
    <div className="space-y-6" data-testid="audit-stats-tab">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-slate-500">Period:</span>
        {[7, 14, 30].map(d => (
          <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'}
            className={days === d ? 'bg-[#DC2626] hover:bg-[#B91C1C] text-white' : ''}
            onClick={() => setDays(d)} data-testid={`period-${d}d`}>
            {d}d
          </Button>
        ))}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Actions', value: stats.total_actions, icon: Activity, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Unique Actions', value: stats.action_breakdown?.length || 0, icon: Shield, color: 'text-[#DC2626]', bg: 'bg-red-50' },
          { label: 'Resource Types', value: stats.resource_breakdown?.length || 0, icon: FileText, color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Active Users', value: stats.top_users?.length || 0, icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
        ].map(s => (
          <Card key={s.label} className="border border-slate-200 rounded-sm shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-sm ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500 tracking-wide">{s.label}</p>
                <p className="text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Action Breakdown + Top Users */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border border-slate-200 rounded-sm shadow-sm">
          <CardHeader className="border-b border-slate-100 p-4">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#DC2626]" /> Action Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {stats.action_breakdown?.length === 0 ? <p className="text-sm text-slate-400">No actions yet</p> : (
              <div className="space-y-2">
                {stats.action_breakdown?.map(a => {
                  const pct = stats.total_actions > 0 ? (a.count / stats.total_actions * 100) : 0;
                  return (
                    <div key={a.action} className="flex items-center gap-3">
                      <Badge className={`text-xs min-w-[100px] justify-center ${ACTION_COLORS[a.action] || 'bg-slate-100 text-slate-700'}`}>{a.action}</Badge>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#DC2626] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-mono text-slate-600 w-8 text-right">{a.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-slate-200 rounded-sm shadow-sm">
          <CardHeader className="border-b border-slate-100 p-4">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#DC2626]" /> Most Active Users
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {stats.top_users?.length === 0 ? <p className="text-sm text-slate-400">No data</p> : (
              <div className="space-y-2">
                {stats.top_users?.map((u, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-400 w-5">{i + 1}.</span>
                      <span className="text-sm">{u.email}</span>
                      <Badge variant="outline" className="text-[10px] font-mono">{u.role}</Badge>
                    </div>
                    <span className="text-sm font-bold font-mono text-slate-700">{u.count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Daily Activity */}
      {stats.daily_activity?.length > 0 && (
        <Card className="border border-slate-200 rounded-sm shadow-sm">
          <CardHeader className="border-b border-slate-100 p-4">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-[#DC2626]" /> Daily Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="flex items-end gap-1 h-32">
              {stats.daily_activity.map((d, i) => {
                const max = Math.max(...stats.daily_activity.map(x => x.count), 1);
                const pct = (d.count / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count} actions`}>
                    <span className="text-[10px] font-mono text-slate-500">{d.count}</span>
                    <div className="w-full bg-slate-100 rounded-sm flex-1 relative" style={{ minHeight: '4px' }}>
                      <div className="absolute bottom-0 left-0 right-0 bg-[#DC2626] rounded-sm transition-all" style={{ height: `${pct}%` }} />
                    </div>
                    <span className="text-[9px] text-slate-400">{d.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


/* ====================================================================
   LOG TABLE TAB
   ==================================================================== */
function LogTable({ entityId }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [distinctActions, setDistinctActions] = useState([]);
  const [distinctResources, setDistinctResources] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/audit-logs/actions`, { headers: h(), credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setDistinctActions(data.actions || []);
          setDistinctResources(data.resource_types || []);
        }
      } catch (e) { console.error(e); }
    })();
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '30' });
      if (entityId) params.append('entity_id', entityId);
      if (search) params.append('search', search);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (resourceFilter !== 'all') params.append('resource_type', resourceFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await fetch(`${API_URL}/api/audit-logs?${params}`, { headers: h(), credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (e) { console.error(e); toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  }, [page, search, actionFilter, resourceFilter, dateFrom, dateTo, entityId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleSearch = (val) => {
    setSearch(val);
    setPage(1);
  };

  return (
    <div className="space-y-4" data-testid="audit-logs-tab">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search logs..." className="pl-10 rounded-sm" value={search}
            onChange={(e) => handleSearch(e.target.value)} data-testid="audit-search" />
        </div>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44 rounded-sm" data-testid="audit-action-filter">
            <Filter className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {distinctActions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={resourceFilter} onValueChange={(v) => { setResourceFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44 rounded-sm" data-testid="audit-resource-filter">
            <SelectValue placeholder="Resource" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            {distinctResources.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          className="w-40 rounded-sm" data-testid="audit-date-from" placeholder="From" />
        <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          className="w-40 rounded-sm" data-testid="audit-date-to" placeholder="To" />
      </div>

      <div className="text-xs text-slate-500">{total} total log entries</div>

      {/* Table */}
      <Card className="border border-slate-200 rounded-sm shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(8)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">No audit logs found</p>
              <p className="text-sm text-slate-400">Adjust your filters or check back later</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 w-40">Timestamp</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500">User</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 w-28">Action</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 w-28">Resource</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500">Details</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider font-semibold text-slate-500 w-32">Resource ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => {
                    const ActionIcon = ACTION_ICONS[log.action] || Activity;
                    const ResIcon = RESOURCE_ICONS[log.resource_type] || FileText;
                    return (
                      <TableRow key={log.audit_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`audit-row-${log.audit_id}`}>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap font-mono">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3 text-slate-400" />
                            {formatTimestamp(log.timestamp)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="text-sm font-medium">{log.user_email}</span>
                            <Badge variant="outline" className="ml-2 text-[10px] font-mono">{log.user_role}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs gap-1 ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-700'}`}>
                            <ActionIcon className="w-3 h-3" />
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 text-sm text-slate-600">
                            <ResIcon className="w-3.5 h-3.5 text-slate-400" />
                            {log.resource_type}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[300px] truncate" title={log.details}>
                          {log.details || '-'}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-slate-400">
                          {log.resource_id || '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="audit-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="audit-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ====================================================================
   MAIN AUDIT TRAIL PAGE
   ==================================================================== */
export default function AuditTrail() {
  const { selectedEntity } = useOutletContext() || {};
  const entityId = selectedEntity?.entity_id;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-trail-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
          Audit Trail
        </h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          Track and monitor all user actions across the HRMS system
        </p>
      </div>

      <Tabs defaultValue="logs" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="logs" data-testid="tab-audit-logs" className="flex items-center gap-1.5">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Activity Logs</span>
            <span className="sm:hidden">Logs</span>
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-audit-stats" className="flex items-center gap-1.5">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Analytics</span>
            <span className="sm:hidden">Stats</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="mt-6">
          <LogTable entityId={entityId} />
        </TabsContent>

        <TabsContent value="stats" className="mt-6">
          <StatsDashboard entityId={entityId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
